import { useEffect, useMemo, useRef, useState } from "react";
import { useApi } from "../api.ts";
import { fmtDateTime, fmtRelative, parseTime } from "../format.ts";
import { Card, cn, EmptyState, LoadError, Loading } from "../ui/index.tsx";
import { GameArt } from "./_shared.tsx";
import { PublicHeading } from "./Games.tsx";
import { IMPACTS, ImpactBadge, incidentScope, LEVEL_ORDER, LEVELS, UpdateTimeline } from "./status/common.tsx";
import type { GameStatusRow, Incident, PublicStatus, StatusDay } from "../../shared/types.ts";

const REFRESH_MS = 60_000;
const MOBILE_DAYS = 30;
const RECENT_DAYS = 14;
const TIP_W = 176;

const DAY_MS = 86_400_000;

// Keyframes live here so the bars animate without touching the global stylesheet.
const BAR_CSS = `
@keyframes status-bar-in { from { opacity: 0; transform: scaleY(0.25); } to { opacity: 1; transform: none; } }
[data-bar] { transform-origin: bottom; }
@media (prefers-reduced-motion: reduce) { [data-bar] { animation: none !important; } }
`;

const fmtDay = (key: string) =>
  parseTime(`${key}T00:00:00Z`)?.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" }) ?? key;

/** Floors rather than rounds so 99.996 never reads as a perfect 100. */
const fmtUptime = (u: number) => (u >= 100 ? "100" : (Math.floor(u * 100) / 100).toFixed(2));

export default function Status() {
  const status = useApi<PublicStatus>("/status");
  const { reload } = status;
  const [fetchedAt, setFetchedAt] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => { if (!document.hidden) void reload(); }, REFRESH_MS);
    return () => clearInterval(id);
  }, [reload]);

  const s = status.data;
  useEffect(() => { if (s) setFetchedAt(Date.now()); }, [s]);

  return (
    <>
      <style>{BAR_CSS}</style>
      <PublicHeading title="Status" kicker="// sys.status // live" body="Live status for every game, with 90 days of history and any incidents we're working on." />
      <div className="mx-auto max-w-6xl px-6 py-8">
        {status.loading && !s ? <Loading /> : status.error && !s ? <LoadError error={status.error} retry={reload} /> : s && (
          <div className="space-y-10">
            <Banner s={s} fetchedAt={fetchedAt} refreshFailed={!!status.error} />
            {s.active_incidents.length > 0 && (
              <section className="space-y-4" aria-label="Active incidents">
                {s.active_incidents.map((i) => <ActiveIncident key={i.id} incident={i} />)}
              </section>
            )}
            <Components games={s.games} windowDays={s.window_days} />
            <PastIncidents incidents={s.recent_incidents} />
          </div>
        )}
      </div>
    </>
  );
}

// ── Banner ──

function Banner({ s, fetchedAt, refreshFailed }: { s: PublicStatus; fetchedAt: number; refreshFailed: boolean }) {
  const meta = LEVELS[s.overall];
  return (
    <div className={cn("hud flex flex-wrap items-center justify-between gap-x-8 gap-y-3 rounded-[var(--radius-card)] border p-5 animate-rise", meta.panel)} role="status">
      <div className="flex items-center gap-4">
        <span className="relative grid size-4 shrink-0 place-items-center">
          <span className={cn("absolute inline-flex size-full animate-ping rounded-full opacity-40", meta.bg)} />
          <span className={cn("relative size-3 animate-pulse-soft rounded-full shadow-[0_0_14px_currentColor]", meta.bg, meta.text)} />
        </span>
        <h2 className={cn("font-display text-2xl font-semibold tracking-tight sm:text-3xl", meta.text)}>{meta.banner}</h2>
      </div>
      <div className="text-sm text-muted sm:text-right">
        <div>{s.last_update ? <>Last loader update <span className="text-fg">{fmtRelative(s.last_update)}</span></> : "No loader published yet"}</div>
        <RefreshTicker at={fetchedAt} failed={refreshFailed} />
      </div>
    </div>
  );
}

/** Own component so the once-a-second tick doesn't re-render the 90-bar rows. */
function RefreshTicker({ at, failed }: { at: number; failed: boolean }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const secs = Math.max(0, Math.floor((now - at) / 1000));
  return (
    <div className="font-mono text-[11px] text-faint">
      Refreshed {secs < 60 ? `${secs}s` : `${Math.floor(secs / 60)}m`} ago
      {failed && <span className="ml-2 text-warn">refresh failed, retrying</span>}
    </div>
  );
}

// ── Active incidents ──

function ActiveIncident({ incident: i }: { incident: Incident }) {
  return (
    <article className={cn("pane overflow-hidden border-l-2 animate-rise", IMPACTS[i.impact].border)}>
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-line px-5 py-4">
        <div className="min-w-0">
          <h3 className={cn("text-lg font-semibold", IMPACTS[i.impact].text)}>{i.title}</h3>
          <p className="mt-0.5 text-xs text-muted">{incidentScope(i)} <span className="mx-1 text-faint">/</span> Started {fmtDateTime(i.created_at)}</p>
        </div>
        <ImpactBadge impact={i.impact} />
      </header>
      <div className="px-5 py-4"><UpdateTimeline updates={i.updates} /></div>
    </article>
  );
}

// ── Components (per game) ──

function Components({ games, windowDays }: { games: GameStatusRow[]; windowDays: number }) {
  return (
    <Card title="Games" description={`Uptime over the past ${windowDays} days`} padded={false}
      actions={<Legend />}>
      {games.length === 0 ? <EmptyState icon="gamepad" title="No games listed yet" /> : (
        <ul className="divide-y divide-line">
          {games.map((g, r) => <GameRow key={g.id} game={g} windowDays={windowDays} delay={r * 60} />)}
        </ul>
      )}
    </Card>
  );
}

function Legend() {
  return (
    <ul className="hidden flex-wrap justify-end gap-x-3 gap-y-1 text-[11px] text-faint lg:flex" aria-label="Legend">
      {LEVEL_ORDER.map((l) => (
        <li key={l} className="flex items-center gap-1.5"><span className={cn("size-2 rounded-[2px]", LEVELS[l].bg)} />{LEVELS[l].label}</li>
      ))}
      <li className="flex items-center gap-1.5"><span className="size-2 rounded-[2px] bg-line" />No data</li>
    </ul>
  );
}

function GameRow({ game: g, windowDays, delay }: { game: GameStatusRow; windowDays: number; delay: number }) {
  const meta = g.level ? LEVELS[g.level] : null;
  const uptime = g.uptime === null ? null : fmtUptime(g.uptime);
  return (
    <li className="px-4 py-4 sm:px-5">
      <div className="flex items-center gap-3">
        <GameArt game={g} className="size-9 shrink-0 rounded-md" />
        <div className="min-w-0 flex-1 truncate font-medium">{g.name}</div>
        <div className="shrink-0 text-right">
          <div className={cn("flex items-center justify-end gap-2 text-sm font-medium", meta ? meta.text : "text-muted")}>
            <span className={cn("size-2 rounded-full", meta ? meta.bg : "bg-faint")} />
            {meta ? meta.label : "In development"}
          </div>
          <div className="font-mono text-[11px] text-faint">{uptime ? `${uptime} % uptime` : "Tracking started recently"}</div>
        </div>
      </div>
      <UptimeBars days={g.days} label={g.name} delay={delay} />
      <div className="mt-1.5 flex items-center justify-between font-mono text-[11px] text-faint">
        <span><span className="sm:hidden">{MOBILE_DAYS}</span><span className="hidden sm:inline">{windowDays}</span> days ago</span>
        <span>{uptime ? `${uptime}% uptime` : ""}</span>
        <span>Today</span>
      </div>
    </li>
  );
}

function UptimeBars({ days, label, delay }: { days: StatusDay[]; label: string; delay: number }) {
  const wrap = useRef<HTMLDivElement>(null);
  const [tip, setTip] = useState<{ i: number; x: number } | null>(null);

  const show = (i: number, el: HTMLElement) => {
    const w = wrap.current;
    if (!w) return;
    const x = el.offsetLeft + el.offsetWidth / 2;
    setTip({ i, x: Math.min(Math.max(x, TIP_W / 2), Math.max(w.clientWidth - TIP_W / 2, TIP_W / 2)) });
  };

  const day = tip ? days[tip.i] : undefined;
  const dayMeta = day?.level ? LEVELS[day.level] : null;

  return (
    <div ref={wrap} className="relative mt-3 flex h-9 gap-[2px]" onMouseLeave={() => setTip(null)} role="img"
      aria-label={`${label}: ${days.length}-day uptime history`}>
      {days.map((d, i) => (
        <div key={d.date} data-bar
          onMouseEnter={(e) => show(i, e.currentTarget)}
          onClick={(e) => show(i, e.currentTarget)}
          style={{ animation: `status-bar-in 450ms cubic-bezier(0.2, 0.8, 0.2, 1) ${delay + i * 6}ms both` }}
          className={cn("min-w-0 flex-1 rounded-[2px] transition-[filter] hover:brightness-125", d.level ? LEVELS[d.level].bg : "bg-line",
            i < days.length - MOBILE_DAYS && "hidden sm:block")} />
      ))}
      {tip && day && (
        <div className="pointer-events-none absolute bottom-full z-20 mb-2 -translate-x-1/2 rounded-lg border border-line-strong bg-raised px-3 py-2 text-xs shadow-xl"
          style={{ left: tip.x, width: TIP_W }}>
          <div className="font-medium">{fmtDay(day.date)}</div>
          <div className={cn("mt-1 flex items-center gap-1.5", dayMeta ? dayMeta.text : "text-muted")}>
            <span className={cn("size-2 rounded-full", dayMeta ? dayMeta.bg : "bg-faint")} />
            {dayMeta ? dayMeta.label : "No data"}
          </div>
          {day.incidents > 0 && <div className="mt-0.5 text-muted">{day.incidents} {day.incidents === 1 ? "incident" : "incidents"}</div>}
        </div>
      )}
    </div>
  );
}

// ── Past incidents ──

function PastIncidents({ incidents }: { incidents: Incident[] }) {
  const groups = useMemo(() => {
    const byDay = new Map<string, Incident[]>();
    for (const i of incidents) {
      const key = i.created_at.slice(0, 10);
      byDay.set(key, [...(byDay.get(key) ?? []), i]);
    }
    const today = Date.now();
    return Array.from({ length: RECENT_DAYS }, (_, n) => {
      const key = new Date(today - n * DAY_MS).toISOString().slice(0, 10);
      return { key, items: byDay.get(key) ?? [] };
    });
  }, [incidents]);

  return (
    <section>
      <div className="mb-5 flex items-end justify-between gap-4 border-b border-line pb-3">
        <h2 className="font-display text-xl font-semibold tracking-wide">Past incidents</h2>
        <span className="kicker">// last {RECENT_DAYS} days</span>
      </div>
      <div className="space-y-6">
        {groups.map((g) => (
          <div key={g.key}>
            <h3 className="mb-2 border-b border-line pb-1.5 font-display text-sm font-semibold tracking-wide text-fg">{fmtDay(g.key)}</h3>
            {g.items.length === 0 ? <p className="text-sm text-faint">No incidents reported.</p> : (
              <div className="space-y-5">
                {g.items.map((i) => (
                  <article key={i.id}>
                    <h4 className={cn("mb-2 text-base font-semibold", IMPACTS[i.impact].text)}>
                      {i.title}
                      <span className="ml-2 text-xs font-normal text-faint">{incidentScope(i)}</span>
                    </h4>
                    <UpdateTimeline updates={i.updates} />
                  </article>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
